import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Group, Pagination, Paper, ScrollArea, Select, Table, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { BiCalendarAlt } from 'react-icons/bi';
import { RiFileExcel2Fill } from 'react-icons/ri';
import client from '../API/API';
import NoDataAvailable from '../components/noDataAvailable/NoDataAvailable';

const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};

const PAGE_SIZE = 31;

const ReportPage = () => {
    const [machines,     setMachines]     = useState([]);
    const [machineId,    setMachineId]    = useState(null);
    const [dateRange,    setDateRange]    = useState([null, null]);
    const [summary,      setSummary]      = useState([]);
    const [colNames,     setColNames]     = useState([]);
    const [visible,      setVisible]      = useState(false);
    const [loading,      setLoading]      = useState(false);
    const [page,         setPage]         = useState(1);
    const [totalPages,   setTotalPages]   = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        client.get('/api/machines/').then(r => setMachines(r.data.machines || [])).catch(console.error);
    }, []);

    const handleSearch = async (nextPage = 1) => {
        if (!machineId || !dateRange[0]) return;
        setLoading(true);
        try {
            const res = await client.get('/api/reports/', {
                params: {
                    machine_id: machineId,
                    start_date: toYMD(dateRange[0]),
                    end_date:   toYMD(dateRange[1] || dateRange[0]),
                    page:       nextPage,
                    page_size:  PAGE_SIZE,
                },
            });
            const rows = res.data.summary || [];
            setSummary(rows);
            setPage(res.data.page || nextPage);
            setTotalPages(res.data.total_pages || 1);
            setTotalRecords(res.data.total_records || 0);

            // Use col_headers from API (already has label overrides applied)
            if (res.data.col_headers?.length) {
                setColNames(res.data.col_headers);
            } else if (rows.length > 0) {
                setColNames(rows[0].ordered_channels.map(c => c.name));
            } else {
                setColNames([]);
            }
            setVisible(true);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handlePageChange = (nextPage) => {
        setPage(nextPage);
        handleSearch(nextPage);
    };

    const handleExport = async () => {
        if (!machineId || !dateRange[0]) return;
        const token = localStorage.getItem('token');
        const s = toYMD(dateRange[0]), e = toYMD(dateRange[1] || dateRange[0]);
        const res = await fetch(
            `/api/reports/export/?machine_id=${machineId}&start_date=${s}&end_date=${e}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `report_${machineId}_${s}_${e}.xlsx`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Container fluid ml={10} mt="0.3rem">
            {/* ── Controls ── */}
            <Box mt={20} p="1rem" pt={0}>
                <Group align="flex-end">
                    <Select
                        label="Machine"
                        placeholder="Select machine"
                        data={machines.map(m => ({ value: m.machine_id, label: m.machine_name || m.machine_id }))}
                        value={machineId}
                        onChange={setMachineId}
                        searchable clearable
                        style={{ minWidth: 220 }}
                    />
                    <DatePickerInput
                        type="range"
                        label="Date Range"
                        placeholder="Pick dates"
                        value={dateRange}
                        onChange={setDateRange}
                        valueFormat="YYYY-MM-DD"
                        labelSeparator=" to "
                        icon={<BiCalendarAlt size="1.3rem" color="var(--color-icon)" />}
                        styles={{ calendar: { width: '220px' }, day: { width: '30px' } }}
                        style={{ minWidth: 260 }}
                    />
                    <Button loading={loading} disabled={!machineId || !dateRange[0]} onClick={() => handleSearch(1)}>
                        Search
                    </Button>
                </Group>
            </Box>

            {/* ── Table ── */}
            {visible && (
                <Container fluid mt={10}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                        <Title color="var(--color-bold-text)" size="1.2rem" weight={500}>Machine Data</Title>
                        <RiFileExcel2Fill
                            color="var(--color-onclick)"
                            style={{ transform:'scale(2)', cursor:'pointer', marginRight:'1rem' }}
                            onClick={handleExport}
                            title="Export to Excel (all data)"
                        />
                    </Box>

                    <Text size="sm" color="var(--color-text)" mb="0.5rem">
                        Showing {summary.length > 0 ? ((page - 1) * PAGE_SIZE) + 1 : 0}–
                        {Math.min(page * PAGE_SIZE, totalRecords)} of {totalRecords} records
                    </Text>

                    <Paper shadow="xs">
                        {summary.length > 0 ? (
                            <>
                                <ScrollArea offsetScrollbars h={460}>
                                    <Table verticalSpacing="md" horizontalSpacing="xl" striped highlightOnHover id="reports-table">
                                        <thead style={{ position:'sticky', top:0, background:'white', zIndex:3 }}>
                                            <tr>
                                                <th style={{ position:'sticky', left:0, background:'white', zIndex:6, minWidth:130 }}>Date</th>
                                                {colNames.map(n => <th key={n} style={{ minWidth:130 }}>{n}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.map((row, i) => {
                                                const vals = {};
                                                (row.ordered_channels || []).forEach(c => { vals[c.name] = c.value; });
                                                return (
                                                    <tr key={i}>
                                                        <td style={{ position:'sticky', left:0, background:'inherit', zIndex:2, fontWeight:500 }}>
                                                            {row.date}
                                                        </td>
                                                        {colNames.map(n => (
                                                            <td key={n}>{vals[n] ?? '—'}</td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </ScrollArea>
                                <Group position="right" p="md">
                                    <Pagination
                                        total={totalPages}
                                        value={page}
                                        onChange={handlePageChange}
                                    />
                                </Group>
                            </>
                        ) : <NoDataAvailable />}
                    </Paper>
                </Container>
            )}
        </Container>
    );
};

export default ReportPage;
